import {
  Fragment, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef,
  useState, type MouseEvent, type TouchEvent,
} from "react";
import { formatTimecode, timecodeAt, type ClockReading } from "@infoflyt/shared/timecode";
import { medSisteOrd, medStoreForkortelser } from "@infoflyt/shared/logg";
import * as db from "../state/db";
import { useSession } from "../state/SessionProvider";
import { skriver } from "../state/skriver";
import type { Merknad } from "../state/tilbakemelding";
import { gjoerPlass, giTilbakePlass } from "../state/kbd";
import { readClock } from "../clock/clock";
import type { Deltaker, LogEntry } from "../state/types";

const LONG_PRESS_MS = 500;
const WRITTEN_KEY = "infoflyt.harSkrevet.v1";

export interface LoggScreenProps {
  /**
   * Det redaktoeren har lagt igjen paa postene.
   *
   * EN MERKNAD KREVER ALDRI NOE. Den staar paa posten, og regi gjoer med den
   * hva hen vil — ingen hake, ingen tilstand, ingenting som kan hindre at
   * dagen avsluttes. Se state/tilbakemelding.ts.
   */
  readonly merknader: readonly Merknad[];
  /** Deltakerne i produksjonen. Trengs for aa skrive et synkspoersmaal. */
  readonly deltakere: readonly Deltaker[];
  /**
   * Skriver et synkspoersmaal ut av en loggpost.
   *
   * Samme kobling redaktoeren lager fra nettflata, og den er verdt mer
   * herfra: regi sto der da det skjedde. Se state/su.ts.
   */
  onNyttSpoersmaal(deltakerId: string | null, tekst: string, kildePostId: string): void;
  /** Rapporterer tidskoden avlesningen i toppen skal vise, eller null for nåtid. */
  onReadoutChange(frames: number | null): void;
  readonly editMode: boolean;
  onEditModeChange(v: boolean): void;
  onWritingChange(frozen: number | null): void;
}

/**
 * Logg-skjermen. Der prosaen skrives.
 *
 * Nyeste post er nederst. Blaing oppover går bakover i tid, og skrivesonen
 * ligger alltid der tommelen er. Loggen åpner nederst, på nåtid.
 */
/*
 * Memo.
 *
 * Klokka gjengir skallet ti ganger i sekundet, og uten dette ble hele
 * loggen avstemt hver gang — inkludert ref-en paa hver rad, som React da
 * koblet fra og paa igjen for hundrevis av poster per sekund mot slutten av
 * et skift. Ingenting her leser klokka; tidskodene regnes fra ankeret.
 * Alle fire prop-ene er stabile.
 */
/** «18:42». Merket trenger ikke sekunder. */
function klokke(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

export const LoggScreen = memo(function LoggScreen({
  onReadoutChange, editMode, onEditModeChange, onWritingChange, merknader,
  deltakere, onNyttSpoersmaal,
}: LoggScreenProps): JSX.Element {
  const {
    entries, anchor, spec, addProse, editEntryText, deleteEntry, oektId, production, shootDay,
  } = useSession();

  /*
   * ========  FORKORTELSENE RETTES MENS DET SKRIVES  ========
   *
   * «chr» blir «CHR». Brukeren 01.11.2026, og grunnen er at regi
   * skriver i moerket med én tommel mens noe skjer — skift-tasten er
   * det foerste som ryker.
   *
   * Lista leses én gang per produksjon og ligger her, ikke i
   * `SessionProvider`: det er loggskjermen som trenger den, og en
   * tilstand til i oekten maatte vaert holdt i takt med synken.
   * Regelen selv ligger i `@infoflyt/shared/logg` — nettflata skriver
   * ogsaa logg, og to kopier av den ville kommet i utakt.
   */
  const [forkortelser, setForkortelser] = useState<readonly string[]>([]);
  useEffect(() => {
    let avbrutt = false;
    void (async () => {
      const d = await db.lastDeltakere(production.id);
      if (!avbrutt) setForkortelser(d.map((x) => x.forkortelse));
    })();
    return () => { avbrutt = true; };
  }, [production.id]);

  /* Merknadene slaas opp paa post. Ett kart er billigere enn et filter per
     post i en logg som kan bli lang. */
  const merknaderPerPost = useMemo(() => {
    const kart = new Map<string, Merknad[]>();
    for (const m of merknader) {
      const liste = kart.get(m.entryId);
      if (liste === undefined) kart.set(m.entryId, [m]); else liste.push(m);
    }
    return kart;
  }, [merknader]);

  const feed = useRef<HTMLDivElement | null>(null);
  const rowEls = useRef<Map<string, HTMLDivElement>>(new Map());
  const pinned = useRef(true);
  const writeBox = useRef<HTMLTextAreaElement | null>(null);
  const spacer = useRef<HTMLDivElement | null>(null);
  /** Skriveboksens plass paa skjermen da posten ble avsluttet. */
  const boxTop = useRef<number | null>(null);
  /** Siste onScroll, saa glidningen kan kalle den uten aa binde seg til den. */
  const onScrollRef = useRef<() => void>(() => undefined);
  /* Lest av lytteren paa visualViewport, som ikke skal bindes til dem. */
  const writingRef = useRef<unknown>(null);
  const editingRef = useRef<string | null>(null);
  /*
   * Radenes plass i innholdet, ikke paa skjermen.
   *
   * Avlesningen leste én boks per rad, fra toppen, for hver rullehendelse.
   * Naer slutten av en lang logg er det hundrevis av oppslag mens fingeren
   * fortsatt er i bevegelse. Plassen i innholdet endrer seg ikke naar man
   * ruller, saa den maales én gang per endring og soekes binaert i.
   */
  const spor = useRef<readonly { top: number; height: number }[] | null>(null);
  /** Sann mens en post lagres, saa «Ferdig» og fokus ut ikke lagrer to ganger. */
  const committing = useRef(false);
  /** Sann mens en ny post glir paa plass. */
  const gliding = useRef(false);
  /**
   * Sann mens loggen glir ned til bunnen etter at tastaturet krympet flata.
   * Rullehendelsene underveis er vaare egne og sier ingenting om hva regi vil.
   */
  const glirTilBunn = useRef(false);
  /** Siste post vi har sett, saa vi kjenner igjen en ny. */
  const lastSeen = useRef<string | null>(null);
  /** Satt av Avbryt i tide til at blur ikke rekker aa lagre. */
  const cancelling = useRef(false);
  /** Y da fingeren gikk ned, for aa kjenne igjen et dra-ned. */
  const dragFrom = useRef<number | null>(null);
  const press = useRef<{
    timer: number; id: number; x: number; y: number; fired: boolean;
    /* Lytteren bor her og ikke i renderen. En ny render lager en ny
       funksjon, og da fjernet vi en annen enn den vi la til — hver
       eneste trykk la igjen en lytter paa dokumentet som holdt paa hele
       postlista. Klokka gjengir ti ganger i sekundet, saa det skjedde
       ved hvert tapp. */
    move: (e: PointerEvent) => void;
  } | null>(null);
  /** Postene vi har sett. Bare en post som ikke staar her er ny. */
  const seen = useRef<Set<string> | null>(null);

  const [scrolledBack, setScrolledBack] = useState(false);
  const [hasWritten, setHasWritten] = useState(
    () => localStorage.getItem(WRITTEN_KEY) === "1",
  );
  const [writing, setWriting] = useState<{ reading: ClockReading; frames: number } | null>(null);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [entryDraft, setEntryDraft] = useState("");
  /**
   * Posten det skrives et synkspoersmaal ut av, med utkastet.
   *
   * ========  «SEND TIL SYNK» LIGGER I REDIGERINGEN  ========
   *
   * Brukerens eget forslag 03.11.2026: «man marker loggposten som om man
   * skal redigere den, saa kommer det opp en Send til synk-knapp til
   * venstre i feltet paa samme linje som avbryt og lagre. Trykker du paa
   * den, saa animeres det inn en tekstboks under feltet, hvor man kan
   * skrive inn spoersmaalet og velge deltaker det gjelder.»
   *
   * Den hoerer hjemme der nettopp fordi den ikke er en egen handling paa
   * loggskjermen: posten er alt valgt, tastaturet er alt oppe, og det er
   * ett trykk fra «jeg skriver om dette» til «dette skal spoerres om».
   * Loggeskjermen faar ingen nye knapper — se CLAUDE.md § «mer paa
   * skjermen er verre i stressede situasjoner».
   */
  const [synkFor, setSynkFor] = useState<string | null>(null);
  const [synkUtkast, setSynkUtkast] = useState("");
  /**
   * Deltakerne spoersmaalet gjelder. Tom betyr ALLE.
   *
   * ========  ETT SPOERSMAAL, FLERE FOLK  ========
   *
   * Brukeren 03.11.2026: «en mulighet til aa gi spoersmaalet til en eller
   * flere deltakere.» Det blir ÉN RAD PER DELTAKER i basen, og det er
   * ikke en omvei: «Hvordan har du det?» til ALI er ikke det samme
   * spoersmaalet som til BEN — det stilles hver for seg, hukes av hver
   * for seg, og staar hver for seg i rapporten. Modellen har sagt det
   * siden 11.10.2026; flata sa det bare ikke.
   */
  const [synkFolk, setSynkFolk] = useState<readonly string[]>([]);
  const synkBoks = useRef<HTMLDivElement | null>(null);

  const rows = useMemo(
    () => entries.map((e) => ({
      entry: e,
      frames: anchor === null ? 0 : timecodeAt(anchor, e, spec).frames,
    })),
    [entries, anchor, spec],
  );

  /*
   * ========  HVOR RAPPORTEN BLE LEVERT  ========
   *
   * Rapporten er et oeyeblikksbilde av det telefonen hadde akkurat da.
   * Skrives loggen videre etterpaa, staar det to sannheter paa samme dag
   * — og brukeren 02.11.2026 ville se hvor skillet gaar: «slik at om du
   * fortsetter aa skrive i loggen, saa kan man se at det ikke er med i
   * rapporten som er levert.»
   *
   * Merket er en STREK og ikke en loggpost. Loggen er append-only og
   * hver rad har én forfatter; en systemskrevet linje ville vaert en rad
   * uten skriver, og den ville gaatt opp til serveren som om noen hadde
   * skrevet den. Tidspunktet leses av `audit_log`. Se db.rapportmerker.
   */
  const [merker, setMerker] = useState<readonly number[]>([]);
  useEffect(() => {
    if (oektId === null) { setMerker([]); return; }
    let avbrutt = false;
    const les = (): void => {
      void (async () => {
        const m = await db.rapportmerker(production.id, oektId);
        if (!avbrutt) setMerker(m);
      })();
    };
    les();
    /* Rapporten leveres fra menyen, ikke herfra. Lista leses derfor om
       naar noe er lagt i koen — samme hendelse synken lytter paa. */
    window.addEventListener("infoflyt:loggkoe", les);
    return () => {
      avbrutt = true;
      window.removeEventListener("infoflyt:loggkoe", les);
    };
  }, [production.id, oektId]);

  /**
   * Limer loggen til bunnen av feltet.
   *
   * Maalet regnes mot vinduet slik det er NAA, ikke slik det blir. Flata
   * vokser over 340 ms naar tastaturet gaar ned, saa et maal mot naavaerende
   * hoeyde krymper jevnt gjennom hele veksten — og innholdet glir ned i takt
   * med at tastaturet forsvinner. Et maal mot full hoeyde faller til
   * sluttverdien i ett bilde, og da smetter hele loggen fire hundre piksler
   * paa plass uten bevegelse. Begge er proevd paa enhet.
   *
   * Under lukkingen kan limet bare rulle NEDOVER. I det posten lagres
   * forsvinner den hoeye skriveboksen, avstandsstykket slutter aa fylle, og
   * innholdets hoeyde hopper — maalt 266 til 314 mot et vindu paa 266. Uten
   * denne begrensningen dro limet loggen 48 px opp for saa aa slippe den ned
   * igjen. Maalet synker uansett naar flata vokser, saa regelen tar bort
   * rykket uten aa roere glidningen.
   */
  const limTilBunn = useCallback((felt: HTMLElement): void => {
    /*
     * REDIGERING LIMER IKKE.
     *
     * Her sto `|| editingRef.current !== null`, og det var feilen brukeren
     * fant 03.11.2026: «naar jeg skal redigere en loggpost i appen, saa
     * dyttes teksten ut av loggfeltet, saa jeg ser ikke lenger hva jeg
     * redigerer.» Skriveboksen staar NEDERST, og limet hoerer den til. En
     * post man retter staar der den staar — med tastaturet oppe er den
     * hoeyere enn det som er igjen av feltet, og limet dro toppen av den
     * (altsaa teksten) ut ovenfor. Effekten under plasserer den i stedet.
     */
    const skriver = writingRef.current !== null;
    const maal = Math.max(0, felt.scrollHeight - felt.clientHeight);
    felt.scrollTop = skriver ? maal : Math.min(felt.scrollTop, maal);
  }, []);

  // Loggen står limt til nåtid med mindre regi selv har blad bort.  // Loggen står limt til nåtid med mindre regi selv har blad bort.
  useEffect(() => {
    if (gliding.current) return;
    if (pinned.current && feed.current !== null) {
      limTilBunn(feed.current);
    }
  }, [rows.length, writing, limTilBunn]);

  /*
   * Maaler skriveboksen og limer loggen til bunnen.
   *
   * Taket maales mot feltet, ikke mot vinduet. `vh` er layout-viewporten, og
   * den krymper ikke naar tastaturet kommer opp — et tak i vh lot boksen bli
   * hoeyere enn plassen den hadde, og da rullet tidskodestempelet i toppen
   * av boksen ut av syne.
   *
   * 124 px er alt som ligger rundt skrivefeltet: postens polstring (20),
   * stempelet (12) og margen over feltet (6), «Ferdig»-raden (44),
   * bunnmargen (14), avstanden i feltet (8) og feltets egen topp-polstring
   * (20).
   */
  const maalSkriveboks = useCallback((maalOm = true): void => {
    const felt = feed.current;
    const el = writeBox.current;
    /*
     * Maalingen er dyr: hoeyde til auto, les scrollHeight, skriv tilbake er
     * to tvungne layouter over hele feltet. Gjoeres bare naar teksten kan ha
     * endret seg, ikke for hvert bilde mens flata krymper — paa en lang logg
     * mot slutten av et skift ville det spist bildene.
     */
    if (maalOm && el !== null && felt !== null) {
      const tak = Math.max(96, felt.clientHeight - 124);
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, tak)}px`;
    }
    /*
     * Limes ogsaa mens en post glir. Glidningen er en transform og endrer
     * ikke scrollHeight, saa de kan ikke slaass — og uten dette samlet det
     * seg en gjeld som oppryddingen betalte i ett sprang.
     */
    if (!pinned.current || felt === null) return;

    /*
     * Ingen egen glidning her.
     *
     * Loekka under kjoerer hvert bilde mens flata endrer hoeyde, og flata har
     * sin egen 340 ms overgang. Settes rullingen mot naavaerende hoeyde hvert
     * bilde, foelger innholdet noeyaktig den overgangen — samme kurve, samme
     * varighet, ingen fare for at to bevegelser kommer i utakt. En egen
     * easing oppaa ville bare lagt en forsinkelse mellom flata og innholdet.
     */
    limTilBunn(felt);
  }, [limTilBunn]);

  /*
   * Tastaturet endrer flatas hoeyde, og da maa boksen maales om og loggen
   * limes til bunnen paa nytt.
   *
   * Boksen plasseres i det skrivingen starter, mens flata fortsatt er i full
   * hoeyde — altsaa der tastaturet er paa vei opp. Naar den visuelle viewporten melder
   * seg krymper flata, men rullingen staar igjen paa den gamle bunnen, og boksen
   * blir staaende under tastaturet.
   *
   * Maalt i en requestAnimationFrame og ikke lagret i tilstand. Hoeyden
   * settes av en annen lytter i App.tsx, og barn kobler seg paa foer
   * foreldre, saa vi maa vente et bilde. Og hendelsen kommer én gang per
   * bilde mens tastaturet animerer: gikk den gjennom tilstand, gjengav vi
   * hele loggen tjue ganger per aapning — presis det memo skulle hindre.
   */
  /*
   * Limer loggen til bunnen saa lenge flata beveger seg.
   *
   * iOS krymper layout-viewporten naar tastaturet kommer, og bruker et kvart
   * sekund paa det. Blir feltets synlige hoeyde mindre, maa rullingen oeke
   * for at bunnen skal bli staaende i syne — ellers glir skriveboksen under
   * kanten mens krympingen pagaar, og foerste tastetrykk limer den tilbake i
   * ett sprang.
   *
   * Loekka startes baade naar skrivingen begynner og naar viewporten melder
   * fra, saa den dekker hele veien uansett hvem som er foerst.
   */
  const foelgRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    let bilde = 0;
    const foelg = (): void => {
      cancelAnimationFrame(bilde);
      /*
       * Loekka daekker baade aapningen og LUKKINGEN.
       *
       * Den gikk foer bare mens det ble skrevet. Da fikk veksten naar
       * tastaturet gaar ned ingen til aa foelge seg, og innholdet ble satt én
       * gang — altsaa et sprang. Flatas overgang er 340 ms, saa 650 daekker
       * den med margin.
       *
       * `glirTilBunn` staar mens loekka gaar: rullehendelsene den lager er
       * vaare egne, og uten dette leste feltet dem som at regi hadde blad
       * bort, slapp limet, og slo paa «Til naa».
       */
      glirTilBunn.current = true;
      const slutt = performance.now() + 650;
      const steg = (): void => {
        maalSkriveboks(false);
        if (performance.now() < slutt) { bilde = requestAnimationFrame(steg); return; }
        glirTilBunn.current = false;
      };
      bilde = requestAnimationFrame(steg);
    };
    foelgRef.current = foelg;

    const vv = window.visualViewport;
    vv?.addEventListener("resize", foelg);
    return () => {
      cancelAnimationFrame(bilde);
      vv?.removeEventListener("resize", foelg);
    };
  }, [maalSkriveboks]);

  /*
   * Starten og slutten paa skrivingen er den andre gangen hoeyden endres.
   * Layout-effekt: refene over maa vaere satt, og loekka maa vaere i gang
   * foer det foerste bildet av krympingen males.
   */
  useLayoutEffect(() => {
    // Baade start og slutt paa skrivingen endrer flatas hoeyde, og begge
    // trenger at noe foelger den hvert bilde.
    foelgRef.current();
  }, [writing]);

  // Boksen vokser med teksten.
  useEffect(() => { maalSkriveboks(); }, [draft, writing, maalSkriveboks]);

  // Alt som kan flytte en rad gjoer tabellen utdatert.
  useEffect(() => { spor.current = null; }, [rows, writing, draft, editMode]);

  /**
   * Avlesningen i toppen følger blaingen og interpolerer mellom postene, slik
   * at tallet spoler jevnt i stedet for å hoppe. Om det føles riktig må prøves
   * på enhet — alternativet er å låse på øverste synlige post hele veien.
   * Se docs/04-timecode.md.
   */
  const onScroll = useCallback((): void => {
    const el = feed.current;
    if (el === null) return;
    /*
     * Rullehendelser mens en post glir sier ingenting om hva regi vil.
     * Naar boksen byttes mot skrivesonen vokser innholdet, og uten dette
     * leste feltet det som at man hadde blad bort: limet slapp, «Til naa»
     * slo paa, og den nye posten ble staaende delvis under kanten.
     *
     * Men limet skal fortsatt kunne slippes: blar regi bort mens posten
     * glir, ville oppryddingen ellers dratt feltet tilbake til bunnen.
     */
    // Vaar egen glidning mot bunnen. Uten dette leste feltet den som at regi
    // hadde blad bort, slapp limet, og glidningen stanset paa halvveien.
    if (glirTilBunn.current) return;
    if (gliding.current) {
      pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
      return;
    }
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    pinned.current = atBottom;

    if (atBottom) {
      if (scrolledBack) { setScrolledBack(false); onReadoutChange(null); }
      return;
    }

    // Bygges om bare naar lista eller hoeydene har endret seg.
    if (spor.current === null) {
      const feltTopp = el.getBoundingClientRect().top;
      spor.current = rows.map((row) => {
        const node = rowEls.current.get(row.entry.id);
        if (node === undefined) return { top: Number.POSITIVE_INFINITY, height: 0 };
        const box = node.getBoundingClientRect();
        return { top: box.top - feltTopp + el.scrollTop, height: box.height };
      });
    }
    const tabell = spor.current;

    // Fadelinja ligger aatte piksler under feltets overkant.
    const linje = el.scrollTop + 8;
    let lo = 0;
    let hi = tabell.length - 1;
    let traff = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const r = tabell[mid];
      if (r === undefined) break;
      if (r.top + r.height > linje) { traff = mid; hi = mid - 1; } else lo = mid + 1;
    }

    let frames: number | null = null;
    const row = traff < 0 ? undefined : rows[traff];
    const boks = traff < 0 ? undefined : tabell[traff];
    if (row !== undefined && boks !== undefined) {
      const nextRow = rows[traff + 1];
      const t = boks.height > 0
        ? Math.min(1, Math.max(0, (el.scrollTop - boks.top) / boks.height))
        : 0;
      frames = nextRow === undefined
        ? row.frames
        : Math.round(row.frames + (nextRow.frames - row.frames) * t);
    }
    if (!scrolledBack) setScrolledBack(true);
    onReadoutChange(frames);
  }, [rows, scrolledBack, onReadoutChange]);
  onScrollRef.current = onScroll;
  writingRef.current = writing;
  editingRef.current = editingId;

  const backToNow = useCallback((): void => {
    const el = feed.current;
    if (el === null) return;
    pinned.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setScrolledBack(false);
    onReadoutChange(null);
  }, [onReadoutChange]);

  /**
   * Tar plassen tilbake naar feltet slippes — men bare naar tastaturet
   * faktisk forsvinner.
   *
   * Hopper regi fra én post til en annen, flyttes fokus videre til et nytt
   * skrivefelt og tastaturet blir staaende. Vokste flata da, ville den vokst
   * inn bak tastaturet. Derfor avgjoeres det i neste bilde, naar fokus har
   * landet.
   */
  const paaSluppet = useCallback((): void => {
    requestAnimationFrame(() => {
      const a = document.activeElement;
      if (a instanceof HTMLTextAreaElement || a instanceof HTMLInputElement) return;
      giTilbakePlass();
    });
  }, []);

  /**
   * Gir feltet fokus uten at nettleseren ruller det inn i syne.
   *
   * Hele rullingen som flytter flata starter her: `focus()` ber nettleseren
   * om aa dra det fokuserte feltet fram, og paa iOS gjoer den det ved aa
   * rulle dokumentet. `autoFocus` kaller `focus()` uten flagg, saa den gikk
   * ikke an aa styre. `preventScroll` finnes nettopp for aa slaa den av.
   *
   * Boksen plasseres alt over tastaturet av hoeyden som settes i trykket, saa
   * det er ingenting aa rulle fram.
   */
  const fokuserUtenRulling = useCallback((el: HTMLTextAreaElement | null): void => {
    if (el === null || document.activeElement === el) return;
    el.focus({ preventScroll: true });
    /*
     * MARKOEREN BAKERST, ikke foerst.
     *
     * Brukeren 03.11.2026: «naar jeg trykker paa en loggpost for aa
     * redigere den, saa kommer markoeren i starten av teksten. Den skal
     * komme inn paa slutten.» Man aapner en post for aa SKRIVE VIDERE —
     * det er hele grunnen til at teksten kommer etterpaa: tidskoden ble
     * fanget i trykket, og setningen fullfoeres naar det er tid. Da skal
     * ikke foerste bokstav havne foran det som alt staar.
     *
     * Samme grep som synkflata bruker naar den aapner et svarfelt.
     */
    const n = el.value.length;
    try {
      el.setSelectionRange(n, n);
    } catch {
      /* Et felt som ikke er i dokumentet ennaa kaster. Da staar markoeren
         der nettleseren setter den, og det er ikke verdt en kraasj. */
    }
  }, []);

  /** Tidskoden låses i trykkøyeblikket, ikke når teksten lagres. */
  const startWriting = useCallback((): void => {
    if (editMode) { onEditModeChange(false); return; }
    /*
     * Plassen til tastaturet gjoeres FOER React rendrer, ikke etterpaa.
     *
     * Skrivefeltet monteres med autoFocus, og iOS avgjoer om siden skal rulles
     * for aa faa det fokuserte feltet fram i samme oeyeblikk fokus settes. En
     * layout-effekt er saa tidlig React kan tilby, og det er for sent: da har
     * iOS alt rullet, og flata — som er `position: fixed` — foelger med, saa
     * toppstripa glir ut av skjermen. Her er vi i selve trykket, og flata er
     * allerede lav naar fokus kommer.
     */
    gjoerPlass();
    const reading = readClock();
    const frames = anchor === null ? 0 : timecodeAt(anchor, reading, spec).frames;
    setWriting({ reading, frames });
    setDraft("");
    onWritingChange(frames);
  }, [anchor, spec, editMode, onEditModeChange, onWritingChange]);

  /*
   * Hele flaten er trykkflate.
   *
   * Regi skriver med én hånd mens noe skjer foran hen. Aa treffe en boks
   * nederst er en presisjonsoppgave som ikke hoerer hjemme der. Trykk hvor
   * som helst i loggen som ikke er en post starter en ny.
   *
   * Postene holdes utenfor: de har sine egne gester, og et trykk paa en
   * post skal aldri lage en ny.
   */
  const onFieldTap = useCallback((e: MouseEvent<HTMLDivElement>): void => {
    if (writing !== null || editingId !== null) return;
    if ((e.target as Element).closest(".entry-row") !== null) return;
    startWriting();
  }, [writing, editingId, startWriting]);

  const finishWriting = useCallback((): void => {
    /*
     * Ett lagringsforsoek per post.
     *
     * «Ferdig» gir foerst fokus ut, som lagrer, og deretter et klikk. Rekker
     * klikket fram til den gamle noden, kjoerer addProse to ganger og loggen
     * faar en tvilling med samme tidskode — i en logg som bare kan legges
     * til maa den da fjernes for haand.
     */
    if (committing.current) return;
    committing.current = true;
    window.setTimeout(() => { committing.current = false; }, 0);
    /*
     * Der boksen staar naa er der den ferdige posten skal gli fra.
     *
     * Bare naar det faktisk blir en post. Ble boksen forlatt tom, endrer
     * ikke lista seg, effekten kjoerer aldri, og verdien ble staaende —
     * saa neste markoer gled inn fra et sted den aldri hadde vaert.
     */
    if (writing !== null && draft.trim().length > 0) {
      /*
       * Maalt mot feltets innhold, ikke mot vinduet.
       *
       * iOS ruller hele siden naar tastaturet kommer og tilbake naar det gaar.
       * To maalinger i vindusrommet — én foer og én etter — havnet derfor i
       * hvert sitt koordinatsystem, og avstanden ble feil eller negativ. Da
       * falt glidningen tilbake paa aatte piksler og saa ut som ingenting.
       */
      const boks = writeBox.current?.closest(".entry");
      const felt = feed.current;
      boxTop.current = boks instanceof HTMLElement && felt !== null
        ? boks.getBoundingClientRect().top - felt.getBoundingClientRect().top + felt.scrollTop
        : null;
    }
    if (writing !== null && draft.trim().length > 0) {
      setHasWritten(true);
      localStorage.setItem(WRITTEN_KEY, "1");
    }
    /* Og det siste ordet. Mens det skrives staar det urort — «chrome»
       skal ikke bli «CHRome» ved tredje bokstav — men naar posten
       lagres er det ferdig. Se @infoflyt/shared/logg. */
    if (writing !== null) addProse(medSisteOrd(draft, forkortelser), writing.reading);
    setWriting(null);
    setDraft("");
    pinned.current = true;
    onWritingChange(null);
  }, [draft, writing, addProse, onWritingChange, forkortelser]);

  /*
   * Dra ned for aa legge vekk tastaturet.
   *
   * To innganger, begge med samme utfall som «Ferdig» og «Lagre»:
   *   - et dra-ned som starter paa skriveboksen, slik det gjoeres i en
   *     meldingsapp
   *   - et dra-ned i loggen naar den ikke kan blas lenger opp, slik at
   *     blaingen alltid vinner saa lenge det er noe igjen aa bla i
   *
   * Touch og ikke pointer: iOS avbryter pekerhendelser i det blaingen tar
   * over, og da slutter de aa komme midt i gesten.
   */
  const onFeedTouchStart = useCallback((e: TouchEvent<HTMLDivElement>): void => {
    dragFrom.current = e.touches[0]?.clientY ?? null;
  }, []);

  const onFeedTouchMove = useCallback((e: TouchEvent<HTMLDivElement>): void => {
    if (writing === null && editingId === null) return;
    const from = dragFrom.current;
    const y = e.touches[0]?.clientY;
    const el = feed.current;
    if (from === null || y === undefined || el === null) return;
    const iBoksen = (e.target as Element).closest(".entry--writing, .entry--open") !== null;
    if (!iBoksen && el.scrollTop > 0) return;
    if (y - from < 56) return;
    dragFrom.current = null;
    (document.activeElement as HTMLElement | null)?.blur();
  }, [writing, editingId]);

  const onFeedTouchEnd = useCallback((): void => { dragFrom.current = null; }, []);

  /*
   * En ny post glir paa plass.
   *
   * To veier inn, og begge skal leses som at posten kommer et sted fra:
   *
   *   - Skrevet i boksen. Boksen staar nederst, rett over tastaturet, mens
   *     posten hoerer hjemme rett etter de andre. Den glir hele den veien.
   *   - Satt som markoer, eller skrevet mens loggen alt fyller flata. Da er
   *     avstanden null, og posten stiger de siste aatte pikslene.
   *
   * Bare den nye posten flyttes. De andre staar allerede riktig.
   *
   * Hva som er nytt avgjoeres av hva vi har sett foer, ikke av hvem som
   * ligger sist. Slettes den nyeste posten, blir en eldre liggende sist —
   * og da animerte den seg inn paa nytt.
   */
  useLayoutEffect(() => {
    const fraTopp = boxTop.current;
    boxTop.current = null;

    const sett = seen.current;
    const naa = new Set(rows.map((r) => r.entry.id));
    seen.current = naa;
    // Foerste gjennomloep merker bare av. En logg som lastes skal ikke
    // animeres inn post for post.
    if (sett === null) return;

    /*
     * Alle nye poster, ikke bare den siste.
     *
     * Settet vet noeyaktig hvilke som er nye, men koden saa bare paa den
     * nederste. Kommer to i samme oppdatering, eller en post som ikke
     * sorterer sist — postene sorteres paa naar tidskoden ble fanget, ikke
     * paa naar de ble lagret — dukket den andre opp uten bevegelse.
     */
    const nye = rows.filter((r) => !sett.has(r.entry.id));
    if (nye.length === 0 || feed.current === null) return;

    const noder: HTMLElement[] = [];
    for (const r of nye) {
      const n = rowEls.current.get(r.entry.id);
      if (n !== undefined) noder.push(n);
    }
    const el = noder[noder.length - 1];
    if (el === undefined) return;

    /*
     * Med redusert bevegelse flyttes ingenting. Posten tones bare inn — en
     * reise over hele flata er nettopp det innstillingen finnes for.
     */
    const rolig = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dy = rolig
      ? 0
      : fraTopp === null
        ? 8
        : (() => {
            const felt = feed.current;
            if (felt === null) return 8;
            const naa = el.getBoundingClientRect().top
              - felt.getBoundingClientRect().top + felt.scrollTop;
            return Math.max(8, Math.round(fraTopp - naa));
          })();

    gliding.current = true;
    /*
     * Kommer posten fra skriveboksen, er den en fortsettelse av den — ikke
     * noe nytt som skal tones inn. Boksen forsvinner i samme bilde, saa en
     * inntoning fra null gir et blink akkurat naar regi ser etter at posten
     * ble lagret. Markoerer har ingenting aa fortsette fra og tones.
     */
    const fortsetter = fraTopp !== null;
    for (const n of noder) {
      // Bare den fra skriveboksen reiser hele veien. De andre stiger.
      const d = n === el ? dy : Math.min(dy, 8);
      n.style.transition = "none";
      n.style.transform = `translate3d(0, ${d}px, 0)`;
      n.style.opacity = fortsetter && n === el ? "1" : "0";
      n.style.willChange = "transform, opacity";
    }
    void el.offsetHeight;            // tving fram starttilstanden
    for (const n of noder) {
      n.style.transition = "transform var(--t-enter) var(--ease-out),"
        + " opacity var(--t-enter) var(--ease-out)";
      n.style.transform = "none";
      n.style.opacity = "1";
    }

    let reserve = 0;
    const rydd = (e?: Event): void => {
      /*
       * Bare radens egen overgang teller. transitionend bobler, og inni raden
       * ligger bade papirkurven og postens egen bakgrunn med egne overganger
       * — gikk man i slettemodus midt i, ryddet vi for tidlig og posten
       * hoppet paa plass.
       */
      if (e !== undefined && e.target !== el) return;
      window.clearTimeout(reserve);
      el.removeEventListener("transitionend", rydd);
      el.removeEventListener("transitioncancel", rydd);
      gliding.current = false;
      for (const n of noder) n.removeAttribute("style");
      const f = feed.current;
      if (pinned.current && f !== null) {
        // Limet ble holdt tilbake mens posten gled. Ta det igjen naa.
        limTilBunn(f);
      } else {
        /*
         * Regi blad bort mens posten gled. Rullehendelsene ble avvist, saa
         * avlesningen i toppen staar fortsatt paa naa-tid og «Til naa» har
         * ikke slaatt paa. Kjoer den én gang naa.
         */
        onScrollRef.current();
      }
    };
    el.addEventListener("transitionend", rydd);
    el.addEventListener("transitioncancel", rydd);
    /*
     * Reserve. Safari leverer ikke transitionend for et skjult dokument, saa
     * laases telefonen midt i bevegelsen ble flagget staaende og
     * auto-rullingen doed til neste post.
     */
    reserve = window.setTimeout(rydd, 600);

    return () => { rydd(); };
  }, [rows, limTilBunn]);

  /*
   * Posten man retter skal VAERE SYNLIG — og ikke flytte seg mer enn det.
   *
   * Redigeringsfeltet er minst 82 px, og med tastaturet oppe er det lite
   * igjen av flata. Det man maa se er teksten man retter, altsaa toppen av
   * posten.
   *
   * MINSTE MULIGE FLYTTING, og ingen flytting naar posten alt staar godt.
   * Foerste utgave dro posten til toppen hver gang, tre ganger etter
   * hverandre — og da hoppet lista under fingeren paa en som bare skulle
   * rette et ord. Brukeren 03.11.2026: «loggpostene hopper rundt paa
   * skjermen naar jeg proever aa trykke meg inn i den.»
   *
   * Maalt mot feltets eget innhold, aldri mot vinduet: iOS ruller hele
   * siden naar tastaturet kommer, og to maalinger i vindusrommet havner i
   * hvert sitt koordinatsystem.
   */
  useEffect(() => {
    if (editingId === null) return;
    const felt = feed.current;
    if (felt === null) return;
    const plasser = (): void => {
      const rad = rowEls.current.get(editingId);
      if (rad === undefined) return;
      const f = felt.getBoundingClientRect();
      const r = rad.getBoundingClientRect();
      const over = r.top - f.top;            // negativ: toppen er utenfor
      const under = r.bottom - f.bottom;     // positiv: bunnen er utenfor
      /* Toppen foerst: er posten hoeyere enn feltet, er det den som maa
         staa — resten kan rulles til. */
      const d = over < 8 ? over - 8 : under > 0 ? Math.min(under + 8, over - 8) : 0;
      if (Math.abs(d) < 2) return;
      const maks = Math.max(0, felt.scrollHeight - felt.clientHeight);
      felt.scrollTop = Math.max(0, Math.min(felt.scrollTop + d, maks));
    };
    plasser();
    /* Feltet skifter hoeyde naar tastaturet kommer. Ett ettersyn, ikke tre:
       vakta over gjoer ingenting naar posten alt staar riktig. */
    const t = window.setTimeout(plasser, 420);
    return () => { window.clearTimeout(t); };
  }, [editingId]);

  /*
   * ========  BOKSEN MAA VAERE PAA SKJERMEN  ========
   *
   * Den staar UNDER posten, og en post midt i en lang logg har ingenting
   * under seg som er synlig naar tastaturet tar halve flata. Brukeren
   * 03.11.2026: «jeg faar fortsatt ikke valgt deltaker» — det er ikke
   * sikkert brikkene manglet, det kan vaere at de sto under tastaturet.
   *
   * Her rulles HELE boksen fram: bunnen foerst, for det er der knappene og
   * deltakerne er. Blir den for hoey til feltet, vinner toppen — da er det
   * skrivefeltet man maa se.
   */
  useEffect(() => {
    if (synkFor === null) return;
    const felt = feed.current;
    if (felt === null) return;
    const plasser = (): void => {
      const el = synkBoks.current;
      if (el === null) return;
      const f = felt.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      const under = b.bottom - f.bottom;
      const over = b.top - f.top;
      const d = b.height > f.height || over < 8
        ? over - 8
        : under > 0 ? under + 8 : 0;
      if (Math.abs(d) < 2) return;
      const maks = Math.max(0, felt.scrollHeight - felt.clientHeight);
      felt.scrollTop = Math.max(0, Math.min(felt.scrollTop + d, maks));
    };
    plasser();
    /* Og en gang til naar tastaturet har satt seg: flata skifter hoeyde,
       og da er regnestykket et annet. */
    const t = window.setTimeout(plasser, 420);
    return () => { window.clearTimeout(t); };
  }, [synkFor]);

  const commitEdit = useCallback((): void => {
    if (editingId !== null) {
      editEntryText(editingId, medSisteOrd(entryDraft, forkortelser));
    }
    setEditingId(null);
    setEntryDraft("");
  }, [editingId, entryDraft, editEntryText, forkortelser]);

  /*
   * Et trykk skal ikke overleve skjermen.
   *
   * Skrus Logg-sida av mens en finger staar paa en post, fyrte uret
   * etterpaa og slo paa slettemodus uten at loggen fantes — og lytteren
   * ble liggende paa dokumentet med hele renderen bak seg.
   */
  useEffect(() => () => { cancelPress(); }, []);

  const beginPress = (e: React.PointerEvent, entry: LogEntry): void => {
    // Slettemodus er alt paa, posten er alt aapen, eller det skrives i
    // boksen. I det siste tilfellet slo slettemodus inn over et aapent
    // tastatur, og hverken trykk utenfor eller Ferdig fikk den bort igjen.
    if (editMode || writing !== null || editingId === entry.id) return;
    // Ett trykk om gangen. To fingre paa hver sin post lot det foerste
    // tidsuret leve videre mot det andre trykket.
    if (press.current !== null) return;

    const x = e.clientX;
    const y = e.clientY;
    /*
     * Bevegelsen leses fra dokumentet, ikke fra posten.
     *
     * Pager laaser aksen ved seks piksler og tar pekeren med
     * setPointerCapture. Fra da av gaar hendelsene til .pager, og posten
     * ser dem aldri — saa grensen ble aldri naadd, trykket ble staaende, og
     * slettemodus slo inn midt i et sidebytte. Paa dokumentet ser vi dem
     * uansett, fordi de bobler opp dit.
     */
    const move = (ev: PointerEvent): void => {
      if (ev.pointerId !== e.pointerId) return;
      if (Math.abs(ev.clientY - y) > 8 || Math.abs(ev.clientX - x) > 8) cancelPress();
    };
    document.addEventListener("pointermove", move);

    press.current = {
      id: e.pointerId,
      x,
      y,
      move,
      fired: false,
      timer: window.setTimeout(() => {
        // Er trykket avbrutt, skal uret ikke gjoere noe. Uten dette slo
        // slettemodus inn av seg selv saa snart man blad i loggen.
        if (press.current === null) return;
        press.current.fired = true;
        onEditModeChange(true);
        setEditingId(null);
      }, LONG_PRESS_MS),
    };
  };

  /*
   * Avbryter et paagaaende langt trykk.
   *
   * Maa stoppe tidsuret, ikke bare glemme trykket: et ur som faar staa
   * fyrer likevel. iOS sender pointercancel i det blaingen tar over, og da
   * er det nettopp blaing og ikke et trykk.
   */
  const cancelPress = (e?: React.PointerEvent): void => {
    const p = press.current;
    // Bare pekeren som startet trykket kan avbryte det. En annen fingers
    // avbrudd stoppet foer et langt trykk som var ment.
    if (p === null || (e !== undefined && p.id !== e.pointerId)) return;
    window.clearTimeout(p.timer);
    document.removeEventListener("pointermove", p.move);
    press.current = null;
  };

  const endPress = (e: React.PointerEvent, entry: LogEntry): void => {
    const p = press.current;
    // Bare pekeren som startet trykket kan avslutte det. Loefter en annen
    // finger foerst, maalte vi dens slipp mot dette trykkets start.
    if (p === null || p.id !== e.pointerId) return;
    press.current = null;
    window.clearTimeout(p.timer);
    document.removeEventListener("pointermove", p.move);
    if (p.fired || editMode) return;
    // En bevegelse er blaing eller et sidebytte, ikke et trykk.
    if (Math.abs(e.clientY - p.y) > 8 || Math.abs(e.clientX - p.x) > 8) return;
    if (editingId !== null && editingId !== entry.id) commitEdit();
    // Samme grunn som i startWriting: plassen foerst, rendringen etterpaa.
    gjoerPlass();
    setEditingId(entry.id);
    setEntryDraft(entry.text);
  };

  return (
    <div className="surface" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="fade" style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div
          className="feed"
          ref={feed}
          onScroll={onScroll}
          onClick={onFieldTap}
          onTouchStart={onFeedTouchStart}
          onTouchMove={onFeedTouchMove}
          onTouchEnd={onFeedTouchEnd}
          onTouchCancel={onFeedTouchEnd}
        >

          {rows.map(({ entry, frames }, radnr) => {
            const isMarker = entry.label.length > 0;
            const editing = editingId === entry.id;
            /* Merkene som ligger MELLOM forrige post og denne. */
            const foer = radnr === 0 ? 0 : rows[radnr - 1]!.entry.wallUtcMs;
            const passerte = merker.filter(
              (m) => m > foer && m <= entry.wallUtcMs);
            return (
              <Fragment key={entry.id}>
              {passerte.map((m) => (
                <div className="rapportmerke" key={m}>
                  <span className="rapportmerke__strek" aria-hidden="true" />
                  <span className="mono rapportmerke__tekst">
                    Rapport levert {klokke(m)} · det under er ikke med
                  </span>
                  <span className="rapportmerke__strek" aria-hidden="true" />
                </div>
              ))}
              <div
                className={`entry-row${editMode ? " entry-row--editing" : ""}`}
                ref={(el) => {
                  if (el === null) rowEls.current.delete(entry.id);
                  else rowEls.current.set(entry.id, el);
                }}
              >
                <button
                  type="button"
                  className="entry-row__trash"
                  onClick={() => deleteEntry(entry.id)}
                  aria-label={`Slett posten ${formatTimecode(frames, spec)}`}
                  tabIndex={editMode ? 0 : -1}
                >
                  ⌫
                </button>

                <div
                  /* Inngangen gjoeres i JS, se effekten over. To
                     animasjoner paa nestede elementer ganget opacity med
                     seg selv og ga en ease-in paa inngangen. */
                  className={`entry${editing ? " entry--open" : ""}`}
                  onPointerDown={(e) => beginPress(e, entry)}
                  onPointerUp={(e) => endPress(e, entry)}
                  onPointerCancel={cancelPress}
                >
                  {isMarker && entry.color !== null ? (
                    <span className="entry__stripe" style={{ background: entry.color }} />
                  ) : null}

                  {isMarker ? (
                    <div className="entry__head">
                      <span className="mono entry__stamp">{formatTimecode(frames, spec)}</span>
                      <span className="mono entry__label">{entry.label}</span>
                      {entry.skrevetAv === null ? null : (
                        <span className="mono entry__hvem">{entry.skrevetAv}</span>
                      )}
                    </div>
                  ) : (
                    <div className="entry__head">
                      <span className="mono entry__stamp">{formatTimecode(frames, spec)}</span>
                      {/* HVEM SOM SKREV DEN, naar det ikke var deg. Teamet
                          deler logg, og uten dette ser man ikke forskjell
                          paa sin egen linje og fotografens. Usynlig paa alt
                          man har skrevet selv — en forkortelse paa hver
                          linje ville vaert stoey. */}
                      {entry.skrevetAv === null ? null : (
                        <span className="mono entry__hvem">{entry.skrevetAv}</span>
                      )}
                    </div>
                  )}

                  {/* Markører uten tekst er ferdige poster. Ingenting telles
                      eller markeres som uferdig. */}
                  {!editing && entry.text.length > 0 ? (
                    <p className="mono entry__text">{entry.text}</p>
                  ) : null}

                  {editing ? (
                    <>
                      {/*
                        * Mister feltet fokus, er endringen lagret — det samme
                        * som «Lagre». Tastaturets egen lukkeknapp gir bare
                        * fokus ut, og da skal ikke posten bli staaende aapen.
                        *
                        * Avbryt maa slippe unna: den setter flagget paa
                        * pointerdown, som kommer foer fokus ut. relatedTarget
                        * duger ikke — iOS gir sjelden fokus til en knapp man
                        * trykker paa, saa den staar tom.
                        */}
                      <textarea
                        className="mono entry__input"
                        value={entryDraft}
                        onChange={(e) => setEntryDraft(
                          medStoreForkortelser(e.target.value, forkortelser))}
                        onBlur={() => {
                          paaSluppet();
                          if (cancelling.current) { cancelling.current = false; return; }
                          commitEdit();
                        }}
                        placeholder="Skriv her"
                        ref={fokuserUtenRulling}
                      />
                      <div style={{
                        display: "flex", justifyContent: "flex-end",
                        alignItems: "center", gap: 18,
                      }}>
                        {/*
                          * SEND TIL SYNK, til venstre paa samme linje.
                          *
                          * ========  LAGRER FOERST, AAPNER ETTERPAA  ========
                          *
                          * Foerste utgave la boksen INNE i redigeringen, og
                          * da tok den livet av seg selv: det nye feltet fikk
                          * fokus, loggfeltet mistet det, `onBlur` lagret og
                          * lukket redigeringen — og boksen forsvant i samme
                          * bilde. Brukeren 03.11.2026: «det jeg har skrevet
                          * i posten forsvinner … og jeg faar ikke valgt
                          * deltaker.»
                          *
                          * Naa lagres teksten HER, redigeringen lukkes, og
                          * boksen aapnes UNDER posten der den lever sitt eget
                          * liv. Posten kan aapnes paa nytt mens boksen staar.
                          */}
                        <button
                          type="button"
                          style={{
                            minHeight: 44, marginRight: "auto",
                            color: "var(--tc)", fontSize: 13,
                          }}
                          /* Fokus skal ikke forlate feltet foer vi har
                             lagret selv — en blur her ville gjort det to
                             ganger. */
                          onPointerDown={(e) => {
                            e.preventDefault();
                            cancelling.current = true;
                          }}
                          onClick={() => {
                            commitEdit();
                            cancelling.current = false;
                            /* Plassen til tastaturet maa settes i trykket,
                               ikke etterpaa: boksens felt faar fokus med én
                               gang, og iOS avgjoer DA om den skal panorere.
                               Se state/kbd.ts. */
                            gjoerPlass();
                            setSynkFor(entry.id);
                            setSynkUtkast("");
                            setSynkFolk([]);
                          }}
                        >
                          Send til synk
                        </button>
                        <button
                          type="button"
                          style={{ minHeight: 44, color: "var(--ink-dim)", fontSize: 13 }}
                          onPointerDown={() => { cancelling.current = true; }}
                          onClick={() => {
                            cancelling.current = false;
                            setEditingId(null);
                            setEntryDraft("");
                            setSynkFor(null);
                          }}
                        >
                          Avbryt
                        </button>
                        <button
                          type="button"
                          style={{ minHeight: 44, color: "var(--tc)", fontSize: 13, fontWeight: 500 }}
                          onClick={commitEdit}
                        >
                          Lagre
                        </button>
                      </div>

                    </>
                  ) : null}
                </div>
              </div>

              {/*
                * ========  REDAKTOERENS SIDE AV SAMTALEN  ========
                *
                * Brukeren 03.11.2026: «Jeg oensker at disse synk-spoersmaalene
                * fra redaktoer og regi og kommentarene fra redaktoer legger
                * seg i loggen i appen slik som de legger seg i ruta til
                * redaktoer under logg. Her vises de mer som en chatt,
                * istedenfor en egen linje under loggposten.»
                *
                * De laa INNE i posten foer, som en strek i margen. Det var
                * feil form: en kommentar er noen som snakker til deg om
                * posten, ikke en del av den. Som bobler til hoeyre kan de
                * heller ikke forveksles med loggen naar rapporten leses.
                *
                * HJERTET BLIR STAAENDE PAA POSTEN. Det er et merke og ikke
                * en replikk — samme sted som i redaktoerens egen flate.
                */}
              {(merknaderPerPost.get(entry.id) ?? []).map((m) => (
                m.slag === "markering" ? (
                  <span className="merknad merknad--hjerte" key={m.id}>
                    ♥ {m.forfatter === "" ? "Hjerte" : `Hjerte fra ${m.forfatter}`}
                  </span>
                ) : (
                  <div
                    className={`svarboble${
                      m.slag === "spoersmaal" ? " svarboble--synk" : ""}`}
                    key={m.id}
                  >
                    {m.slag === "spoersmaal" ? (
                      <span className="mono svarboble__merke">
                        SYNK · {m.forfatter === "" ? "ALLE" : m.forfatter}
                      </span>
                    ) : null}
                    <p className="svarboble__tekst">{m.tekst}</p>
                    {m.slag === "spoersmaal" ? null : (
                      <span className="svarboble__hvem">{m.forfatter}</span>
                    )}
                  </div>
                )
              ))}

              {/*
                * ========  SPOERSMAALET REGI SKRIVER SELV  ========
                *
                * Staar UNDER posten og utenfor den, som boblene over —
                * og utenfor redigeringen, som tok livet av den foerste
                * utgaven. Boksen blir staaende til den sendes eller
                * avbrytes, og posten kan aapnes og rettes imens.
                */}
              {synkFor !== entry.id ? null : (
                <div className="synkboks" ref={synkBoks}>
                  <span className="synkboks__tittel mono">
                    SYNKSPØRSMÅL · {formatTimecode(frames, spec)}
                  </span>
                  {/* Eget felt, og det sier hva det er. Teksten her blir
                      spoersmaalet — posten over er urort. */}
                  <textarea
                    className="mono synkboks__felt"
                    value={synkUtkast}
                    placeholder="Skriv spørsmålet her"
                    aria-label="Synkspørsmål"
                    autoFocus
                    onChange={(e) => { setSynkUtkast(e.target.value); }}
                  />
                  <span className="synkboks__merkelapp">
                    {deltakere.length === 0
                      ? "Ingen deltakere er lagt inn i produksjonen ennå."
                        + " Spørsmålet går til alle."
                      : "Hvem gjelder det? Velg én eller flere."}
                  </span>
                  {/*
                    * Deltakerne som brikker, og FLERE om gangen. Ingen
                    * valgt betyr alle — et generelt spoersmaal, som i
                    * SU-lista. En systemvelger er et treff man maa sikte
                    * paa, og flata brukes med én haand paa et sett.
                    */}
                  <div className="synkboks__folk">
                    <button
                      type="button"
                      className={`synkboks__person${
                        synkFolk.length === 0 ? " synkboks__person--valgt" : ""}`}
                      onPointerDown={(e) => { e.preventDefault(); }}
                      onClick={() => { setSynkFolk([]); }}
                    >
                      Alle
                    </button>
                    {deltakere.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        className={`mono synkboks__person${
                          synkFolk.includes(d.id) ? " synkboks__person--valgt" : ""}`}
                        /* Fokus blir i skrivefeltet: velger man folk midt i
                           en setning, skal tastaturet staa. */
                        onPointerDown={(e) => { e.preventDefault(); }}
                        onClick={() => {
                          setSynkFolk((f) => (f.includes(d.id)
                            ? f.filter((x) => x !== d.id) : [...f, d.id]));
                        }}
                      >
                        {d.forkortelse}
                      </button>
                    ))}
                  </div>
                  <div className="synkboks__knapper">
                    <button
                      type="button"
                      className="synkboks__avbryt"
                      onClick={() => { setSynkFor(null); setSynkUtkast(""); }}
                    >
                      Avbryt
                    </button>
                    <button
                      type="button"
                      className="synkboks__send"
                      disabled={synkUtkast.trim() === ""}
                      onClick={() => {
                        const tekst = synkUtkast.trim();
                        if (tekst === "") return;
                        /* ÉN RAD PER DELTAKER. Ingen valgt gir ett generelt
                           spoersmaal — det staar oeverst hos alle. */
                        const folk: readonly (string | null)[] =
                          synkFolk.length === 0 ? [null] : synkFolk;
                        for (const id of folk) onNyttSpoersmaal(id, tekst, entry.id);
                        setSynkFor(null);
                        setSynkUtkast("");
                        setSynkFolk([]);
                      }}
                    >
                      {synkFolk.length > 1
                        ? `Legg i synklista (${synkFolk.length})`
                        : "Legg i synklista"}
                    </button>
                  </div>
                </div>
              )}
              </Fragment>
            );
          })}

          {/*
            * Mellomrommet ligger etter postene og foer boksen, saa det
            * dytter bare boksen ned. Postene staar der de staar.
            *
            * Det vokser bare mens det skrives — da faar boksen den ledige
            * plassen og havner nederst, rett over tastaturet.
            */}
          <div
            ref={spacer}
            className={`feed__spacer${writing !== null ? " feed__spacer--grow" : ""}`}
          />

          {writing !== null ? (
            <div className="entry entry--writing" style={{ marginBottom: 14 }}>
              <div className="mono entry__stamp">{formatTimecode(writing.frames, spec)}</div>
              {/*
                * Mister feltet fokus, er posten ferdig.
                *
                * Tastaturets egen lukkeknapp gir bare blur — uten dette ble
                * boksen staaende aapen uten tastatur, og «Ferdig» var det
                * eneste som lukket den. Det er to maater aa avslutte paa der
                * regi med rimelighet venter én.
                *
                * Trygt fordi addProse avviser tom tekst: lukking uten
                * innhold lager ingen post. Trykk paa «Ferdig» gir blur
                * foerst, men finishWriting taaler aa kjoere to ganger.
                */}
              <textarea
                ref={(el) => {
                  writeBox.current = el;
                  fokuserUtenRulling(el);
                }}
                className="mono entry__input"
                value={draft}
                onChange={(e) => {
                  setDraft(medStoreForkortelser(e.target.value, forkortelser));
                  /* Sier fra til redaktoeren at det skrives. TEKSTEN SENDES
                     ALDRI — bare at det skjer. Se state/skriver.ts. */
                  skriver(production.id, shootDay, oektId);
                }}
                onBlur={() => { paaSluppet(); finishWriting(); }}
                placeholder="Skriv her"
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  style={{ minHeight: 44, color: "var(--ink)", fontSize: 13, fontWeight: 500 }}
                  onClick={finishWriting}
                >
                  Ferdig
                </button>
              </div>
            </div>
          ) : (
            /* Ingen stiplet ramme og ingen boks — hele flaten utenfor postene
               er trykkflate. */
            /* Knappen beholdes for navn og tastaturtilgang. Trykket tas av
               .feed, som klikket bobler opp til, slik at hele flaten virker. */
            <button
              type="button"
              className="writezone"
              aria-label="Skriv ny loggpost"
            >
              {/* Hintet står til brukeren har skrevet sin første post.
                  Deretter forsvinner det for godt — en instruksjon for noe
                  man lærer den første dagen skal ikke stå permanent.
                  Se docs/13-design.md. */}
              {hasWritten ? null : <span className="mono">Trykk for å skrive</span>}
            </button>
          )}
        </div>
      </div>

      <div className={`tonow${scrolledBack ? " tonow--visible" : ""}`}>
        <button type="button" onClick={backToNow}>Til nå</button>
      </div>
    </div>
  );
});
